import { IPoint2d } from './IPoint2d';
import { Curve2dUtils } from './Curve2dUtils';

interface ICell {
  x: number;
  y: number;
  /** 半边长 */
  h: number;
  /** 中心点到边界的有符号距离 */
  d: number;
  /** 该单元格内可能的最大距离上界 */
  max: number;
}

/**
 * Mapbox polylabel 算法：计算带洞多边形的"最难抵达点"（pole of inaccessibility），
 * 即距离所有边界（含洞）最远的内部点，等价于最大内切圆圆心。
 *
 * 四叉树逐级细分候选单元格，按距离上界剪枝，精度由 precision 控制。
 * 参考 https://github.com/mapbox/polylabel
 */
export class Polylabel {
  /**
   * @param rings 多边形环数组：[外轮廓, ...洞]
   * @param precision 结果精度（与坐标同单位）
   * @returns center 最难抵达点，distance 到边界的距离（即内切圆半径）
   */
  static findPoleOfInaccessibility(rings: IPoint2d[][], precision: number = 1): { center: IPoint2d; distance: number } {
    const exterior = rings[0];

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const p of exterior) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }

    const width = maxX - minX;
    const height = maxY - minY;
    // 下限取 precision，避免细长多边形初始格子数爆炸
    const cellSize = Math.max(precision, Math.min(width, height));

    if (cellSize === precision) {
      // 包围盒最小边不超过精度，退化为包围盒中心
      return { center: { x: minX + width / 2, y: minY + height / 2 }, distance: 0 };
    }

    // 用初始单元格覆盖整个包围盒
    const cellQueue: ICell[] = [];
    const h = cellSize / 2;
    for (let x = minX; x < maxX; x += cellSize) {
      for (let y = minY; y < maxY; y += cellSize) {
        this._enqueue(cellQueue, this._createCell(x + h, y + h, h, rings));
      }
    }

    // 以质心和包围盒中心作为初始最优解
    const centroid = Curve2dUtils.calculatePolygonWithHolesCentroid(exterior);
    let bestCell = this._createCell(centroid.x, centroid.y, 0, rings);
    const bboxCell = this._createCell(minX + width / 2, minY + height / 2, 0, rings);
    if (bboxCell.d > bestCell.d) {
      bestCell = bboxCell;
    }

    while (cellQueue.length) {
      const cell = this._dequeue(cellQueue);

      if (cell.d > bestCell.d) {
        bestCell = cell;
      }

      // 堆按 max 降序，堆顶都不可能比当前最优解好 precision 以上时，后续更不可能
      if (cell.max - bestCell.d <= precision) break;

      const half = cell.h / 2;
      this._enqueue(cellQueue, this._createCell(cell.x - half, cell.y - half, half, rings));
      this._enqueue(cellQueue, this._createCell(cell.x + half, cell.y - half, half, rings));
      this._enqueue(cellQueue, this._createCell(cell.x - half, cell.y + half, half, rings));
      this._enqueue(cellQueue, this._createCell(cell.x + half, cell.y + half, half, rings));
    }

    return { center: { x: bestCell.x, y: bestCell.y }, distance: bestCell.d };
  }

  private static _createCell(x: number, y: number, h: number, rings: IPoint2d[][]): ICell {
    const d = this._signedDistanceToBoundary({ x, y }, rings);
    return { x, y, h, d, max: d + h * Math.SQRT2 };
  }

  /**
   * 点到多边形边界的有符号距离：多边形内部（且不在洞内）为正，否则为负
   */
  private static _signedDistanceToBoundary(point: IPoint2d, rings: IPoint2d[][]): number {
    let minDistance = Infinity;
    for (const ring of rings) {
      minDistance = Math.min(minDistance, Curve2dUtils.pointToPolygonDistance(point, ring));
    }

    const [exterior, ...holes] = rings;
    const inside =
      Curve2dUtils.isPointInPolygon(point, exterior) &&
      !holes.some((hole) => Curve2dUtils.isPointInPolygon(point, hole));

    return inside ? minDistance : -minDistance;
  }

  // 以 max 为优先级的二叉最大堆
  private static _enqueue(heap: ICell[], cell: ICell): void {
    heap.push(cell);
    let i = heap.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (heap[parent].max >= heap[i].max) break;
      [heap[parent], heap[i]] = [heap[i], heap[parent]];
      i = parent;
    }
  }

  private static _dequeue(heap: ICell[]): ICell {
    const top = heap[0];
    const last = heap.pop();
    if (last && heap.length) {
      heap[0] = last;
      let i = 0;
      for (;;) {
        const left = 2 * i + 1;
        const right = left + 1;
        let largest = i;
        if (left < heap.length && heap[left].max > heap[largest].max) largest = left;
        if (right < heap.length && heap[right].max > heap[largest].max) largest = right;
        if (largest === i) break;
        [heap[largest], heap[i]] = [heap[i], heap[largest]];
        i = largest;
      }
    }
    return top;
  }
}
